/**
 * Collection filter enhancement
 *
 * This script extends Shopify collection filtering to include
 * products marked with `custom.is_reserved = true` when the
 * "Out of stock" availability filter is active.
 *
 * Implemented as a client-side OR-merge due to Shopify limitations.
 * Do not remove without verifying subscription / reserved flows.
 */

(() => {
  const OOS_PARAM = "filter.v.availability";
  const OOS_VALUE = "0";
  const RESERVED_PARAM = "filter.p.m.custom.is_reserved";
  const RESERVED_VALUE = "1";

  const GRID_SELECTOR = ".collection-matrix";
  const CARD_SELECTOR = ".product__thumbnail.thumbnail";
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';

  function urlHasOOS(urlString) {
    const u = new URL(urlString, window.location.origin);
    return u.searchParams.getAll(OOS_PARAM).includes(OOS_VALUE);
  }

  function buildReservedUrl(fromUrlString) {
    const u = new URL(fromUrlString, window.location.origin);
    u.searchParams.delete(OOS_PARAM);
    u.searchParams.set(RESERVED_PARAM, RESERVED_VALUE);
    return u.toString();
  }

  function cardKey(card) {
    const a = card.querySelector(PRODUCT_LINK_SELECTOR);
    return (
      a?.getAttribute("href") ||
      card.getAttribute("data-product-id") ||
      card.textContent.trim().slice(0, 80)
    );
  }

  let inProgress = false;

  async function mergeReserved(fromUrlString) {
    if (inProgress) return;
    if (!urlHasOOS(fromUrlString)) return;

    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return;

    inProgress = true;
    try {
      const currentCards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
      const seen = new Set(currentCards.map(cardKey));

      const reservedUrl = buildReservedUrl(fromUrlString);
      const res = await fetch(reservedUrl, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) return;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const reservedGrid = doc.querySelector(GRID_SELECTOR);
      if (!reservedGrid) return;

      const reservedCards = Array.from(
        reservedGrid.querySelectorAll(CARD_SELECTOR)
      );

      const frag = document.createDocumentFragment();
      let appended = 0;

      for (const card of reservedCards) {
        const key = cardKey(card);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        frag.appendChild(card.cloneNode(true));
        appended++;
      }

      if (appended) grid.appendChild(frag);
    } finally {
      inProgress = false;
    }
  }

  function patchUpdateView() {
    const jsCollection = window.PXUTheme?.jsCollection;
    if (!jsCollection || typeof jsCollection.updateView !== "function") return;

    const original = jsCollection.updateView.bind(jsCollection);

    jsCollection.updateView = function (filterURL) {
      const ret = original(filterURL);
      Promise.resolve(ret).then(() => mergeReserved(filterURL));
      return ret;
    };
  }

  function hookAjaxComplete() {
    if (!window.jQuery) return;
    jQuery(document).ajaxComplete(() => {
      mergeReserved(window.location.href);
    });
  }

  window.addEventListener("DOMContentLoaded", () => {
    patchUpdateView();
    hookAjaxComplete();
    mergeReserved(window.location.href);
  });

  window.addEventListener("popstate", () => {
    mergeReserved(window.location.href);
  });
})();
